// useBuilder — the wizard's working-state controller (slice 4a-3).
//
// Holds the local WorkingDraft, exposes an `update` that merges a partial and
// debounce-autosaves the diff to the API. The server Program (status,
// activated_at, …) stays owned by the `useProgram` query; this hook only
// drives the editable fields.

import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { Milestone, BackLink, ProgramDraftPatch } from '@naya/shared'

import { OwnerApiError } from '../../ownerApi.js'
import {
  draftDiff,
  isTypedDraft,
  programToDraft,
  reconcileExpiration,
  reconcileFrontFields,
  reconcileMilestones,
  type WorkingDraft,
} from '../builderState.js'
import { usePatchProgram } from './useProgram.js'
import type { Program } from '../types.js'

const AUTOSAVE_DEBOUNCE_MS = 600

/**
 * Does this draft have any milestones / back-links that the server's Zod will
 * reject (empty `reward_text`, empty link `value`/`display_text`)? If so, the
 * autosave defers until the owner fills them in — otherwise we'd send a 422
 * the moment they click "Add", which (a) confuses the save indicator and
 * (b) feeds the wrong server response back into the re-baseline effect.
 *
 * Empty placeholders are a UI-only state — once the owner types the reward
 * text or the link URL, the draft validates and the next debounced save fires.
 */
function hasInvalidPlaceholders(draft: WorkingDraft): boolean {
  // Milestone reward text only gates loyalty saves — a typed (membership /
  // event) draft has no milestones (it carries loyalty-default placeholders we
  // never patch).
  if (draft.pass_type === 'loyalty') {
    const milestoneEmpty = draft.milestones.some(
      (m: Milestone) => m.reward_text.trim().length === 0,
    )
    if (milestoneEmpty) return true
  }
  const linkEmpty = draft.back_links.some(
    (l: BackLink) =>
      l.value.trim().length === 0 || l.display_text.trim().length === 0,
  )
  return linkEmpty
}

// `pass_type` is a draft-only discriminant, never a patch field. The loyalty
// structural keys are rejected by the API on a membership program, and
// `type_config` is rejected on a loyalty program — so we strip the keys that
// don't belong to the program's type before sending the diff.
const LOYALTY_ONLY_PATCH_KEYS = [
  'total_stamps',
  'completion_behavior',
  'milestones',
  'referral_config',
] as const
// type_config is rejected on a loyalty program. Membership + event both carry it.
const TYPE_CONFIG_PATCH_KEYS = ['type_config'] as const

/** Strip draft-only / wrong-type keys from a diff so the PATCH body is valid
 *  for the program's pass type. `typed` = a non-loyalty (membership/event)
 *  program, which carries type_config and rejects the loyalty structural keys. */
function scopePatchToType(
  diff: Partial<WorkingDraft>,
  typed: boolean,
): Partial<WorkingDraft> {
  const scoped: Partial<WorkingDraft> = { ...diff }
  // `pass_type` is never patchable.
  delete (scoped as Record<string, unknown>).pass_type
  const drop = typed ? LOYALTY_ONLY_PATCH_KEYS : TYPE_CONFIG_PATCH_KEYS
  for (const key of drop) delete (scoped as Record<string, unknown>)[key]
  return scoped
}

export type SaveState =
  | { status: 'idle' }
  | { status: 'saving' }
  | { status: 'saved' }
  | { status: 'error'; message: string; lockedFields?: string[] }

export interface BuilderController {
  /** The id of the program being edited — threaded to id-scoped hooks
   *  (hero upload, etc.) in the steps. */
  programId: string
  draft: WorkingDraft
  /** Merge a partial into the draft. Triggers a debounced autosave. */
  update: (partial: Partial<WorkingDraft>) => void
  /** Like `update`, but flushes the save immediately (used on step change). */
  updateAndFlush: (partial: Partial<WorkingDraft>) => void
  /** Force-flush any pending debounced save now. Returns when the PATCH settles. */
  flush: () => Promise<void>
  saveState: SaveState
}

export function useBuilder(program: Program): BuilderController {
  const patch = usePatchProgram(program.id)
  // The working draft — initialised from the server program, then locally owned.
  const [draft, setDraft] = useState<WorkingDraft>(() => programToDraft(program))
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle' })

  // Last value we successfully synced to the server — the diff baseline.
  const syncedRef = useRef<WorkingDraft>(draft)
  // The latest draft, for the debounce timer to read without a stale closure.
  const draftRef = useRef<WorkingDraft>(draft)
  draftRef.current = draft
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // `updated_at` of the server program we last saw — used to distinguish
  // "the server changed because of our own PATCH" (skip re-baseline; would
  // clobber edits typed during the round-trip) from "the server changed for
  // some other reason like activation or a focus refetch" (do re-baseline).
  const lastSeenUpdatedAtRef = useRef<string>(program.updated_at)

  // Re-baseline only when the server program changed from a source OTHER
  // than our own autosave. Our PATCH's onSuccess updates the cache, which
  // emits a new `program` here with a new `updated_at` — but the response
  // is a snapshot from when the server received our diff, NOT from when
  // it returned. If the owner kept typing during the round-trip, those
  // post-snapshot edits live only in `draft`; re-baselining would erase them.
  //
  // We mark our own PATCH responses by setting `lastSeenUpdatedAtRef` to
  // `res.program.updated_at` inside doSave's success path. On the next
  // emission, this effect compares: if the incoming updated_at matches the
  // ref, it's our own save — skip. Otherwise (activation, refetch, etc.),
  // re-baseline.
  useEffect(() => {
    if (program.updated_at === lastSeenUpdatedAtRef.current) return
    lastSeenUpdatedAtRef.current = program.updated_at
    const fresh = programToDraft(program)
    syncedRef.current = fresh
    setDraft(fresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.id, program.updated_at])

  const doSave = useCallback(async (): Promise<void> => {
    const next = draftRef.current
    const diff = scopePatchToType(
      draftDiff(syncedRef.current, next),
      isTypedDraft(next),
    )
    if (Object.keys(diff).length === 0) return

    // Defer the save if the draft contains empty placeholder rows the server
    // would 422 on (milestones with empty reward_text, back_links with empty
    // value/display_text). The next keystroke that completes them schedules
    // another save.
    if (hasInvalidPlaceholders(next)) {
      setSaveState({ status: 'idle' })
      return
    }

    setSaveState({ status: 'saving' })
    try {
      const res = await patch.mutateAsync(diff as ProgramDraftPatch)
      syncedRef.current = next
      // Record the server's updated_at for our own save so the re-baseline
      // effect skips it. Without this, the effect would clobber any edits
      // the owner typed between PATCH-send and PATCH-return.
      lastSeenUpdatedAtRef.current = res.program.updated_at
      setSaveState({ status: 'saved' })
      // Transient acknowledgment so the owner gets feedback on each save.
      // doSave is only reachable from user actions (debounced edit + step-nav
      // flush), so this never surprises with phantom toasts from a focus
      // refetch or cache rebuild. Inline status keeps the persistent signal.
      toast.success('Saved', { duration: 1500 })
    } catch (err) {
      if (err instanceof OwnerApiError && err.code === 'STRUCTURAL_LOCKED') {
        const lockedFields = Array.isArray(err.details?.fields)
          ? (err.details.fields as string[])
          : undefined
        setSaveState({
          status: 'error',
          message:
            'Some fields are locked now that the program is active and were not saved.',
          ...(lockedFields ? { lockedFields } : {}),
        })
      } else {
        setSaveState({
          status: 'error',
          message:
            err instanceof OwnerApiError
              ? err.userMessage
              : 'Could not save — please try again.',
        })
      }
    }
  }, [patch])

  const scheduleSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      void doSave()
    }, AUTOSAVE_DEBOUNCE_MS)
  }, [doSave])

  const flush = useCallback(async (): Promise<void> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    await doSave()
  }, [doSave])

  // Merge helper — keeps derived fields consistent:
  //  - a `total_stamps` change re-pins the grand prize + clamps milestones
  //  - an `onboarding_fields` change prunes now-disabled pass-front fields
  const applyPartial = useCallback(
    (partial: Partial<WorkingDraft>): WorkingDraft => {
      let merged = { ...draftRef.current, ...partial }
      // Loyalty-only reconcilers — a typed (membership/event) draft has no
      // stamps/milestones, so we skip them. onboarding_fields → front_fields
      // pruning applies to all types.
      if (merged.pass_type === 'loyalty') {
        if (partial.total_stamps !== undefined) {
          merged = {
            ...merged,
            milestones: reconcileMilestones(
              merged.milestones,
              partial.total_stamps,
            ),
          }
        }
        // A completion_behavior change away from 'stay_completed' clears any
        // expiry config — expiration is only valid on a stay_completed loyalty
        // program. Memberships can expire in any mode.
        if (partial.completion_behavior !== undefined) {
          merged = reconcileExpiration(merged)
        }
      }
      if (partial.onboarding_fields !== undefined) {
        merged = reconcileFrontFields(merged)
      }
      return merged
    },
    [],
  )

  const update = useCallback(
    (partial: Partial<WorkingDraft>) => {
      const merged = applyPartial(partial)
      setDraft(merged)
      draftRef.current = merged
      scheduleSave()
    },
    [applyPartial, scheduleSave],
  )

  const updateAndFlush = useCallback(
    (partial: Partial<WorkingDraft>) => {
      const merged = applyPartial(partial)
      setDraft(merged)
      draftRef.current = merged
      void flush()
    },
    [applyPartial, flush],
  )

  // Flush on unmount so a half-typed edit isn't lost when the owner leaves.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        void doSave()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { programId: program.id, draft, update, updateAndFlush, flush, saveState }
}
